import { Navigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAccessToken } from "../redux/authSlice";
import { useAccessToken } from "../hooks/useAccessToken";

/**
 * ProtectedRoute
 *
 * A wrapper component that protects routes requiring authentication.
 * If the user is not authenticated, they are redirected to the Authentication page.
 * If authenticated, ensures the access token is stored in Redux and user is validated.
 */
export default function ProtectedRoute({ children }) {
  const { accounts, inProgress, instance } = useMsal();
  const location = useLocation();
  const dispatch = useDispatch();
  const { getAccessToken } = useAccessToken();
  const accessToken = useSelector((state) => state?.auth?.accessToken);
  const [isValidating, setIsValidating] = useState(false);

  // Generate a unique session ID if it doesn't exist - MUST run before any rendering
  const generateSessionId = () => {
    const uuidPattern = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
    return uuidPattern.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  // Ensure sessionId exists BEFORE any component renders
  // This runs synchronously during component initialization
  let sessionId = sessionStorage.getItem("sessionId");
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem("sessionId", sessionId);
    console.log("ProtectedRoute: Generated NEW sessionId:", sessionId);
  } else {
    console.log("ProtectedRoute: Using EXISTING sessionId:", sessionId);
  }

  // Helper function to get the sessionId
  const ensureSessionId = () => {
    const id = sessionStorage.getItem("sessionId");
    console.log("ProtectedRoute: ensureSessionId returning:", id);
    return id;
  };

  // Validate user and set userId/roles in sessionStorage
  const validateUser = async () => {
    try {
      const sessionId = ensureSessionId();
      console.log("ProtectedRoute: Using sessionId for validation:", sessionId);
      
      if (!sessionId) {
        console.error("ProtectedRoute: Failed to get or create session ID");
        return false;
      }

      const token = await getAccessToken(["User.Read"]);
      const login_endpoint = process.env.REACT_APP_AVATAR_API_LOGIN_ENDPOINT;

      console.log("ProtectedRoute: Calling login API with payload:", { unique_id: sessionId });

      const response = await fetch(login_endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ unique_id: sessionId }),
      });

      if (response.status === 200) {
        const data = await response.json();

        if (data?.message === "False") {
          return false;
        }

        if (Array.isArray(data) && data.length > 0) {
          const userInfo = data[0];

          if (userInfo.UserId) {
            sessionStorage.setItem("userId", userInfo.UserId);
            console.log("ProtectedRoute: userId set in sessionStorage");
          }

          if (userInfo.Roles) {
            const rolesString = userInfo.Roles;
            const roles = rolesString
              .split(";")
              .map((role) => role.replace(/^[^-]*-/, "").trim())
              .filter(Boolean);

            sessionStorage.setItem("userRoles", JSON.stringify(roles));
            console.log("ProtectedRoute: userRoles set in sessionStorage");
          }

          return true;
        }

        return false;
      } else {
        return false;
      }
    } catch (error) {
      console.error("ProtectedRoute: User validation failed:", error);
      return false;
    }
  };

  // Call loadData API after successful validation
  const callLoadDataAPI = async (token) => {
    try {
      const sessionId = ensureSessionId();
      console.log("ProtectedRoute: Using sessionId for loadData:", sessionId);
      
      const loadDataEndpoint = process.env.REACT_APP_AVATAR_API_LOAD_DATA_ENDPOINT;

      console.log("ProtectedRoute: Calling loadData API with payload:", { unique_id: sessionId });

      const response = await fetch(loadDataEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ unique_id: sessionId }),
      });

      if (response.status === 200) {
        console.log("ProtectedRoute: LoadData API called successfully");
      } else {
        console.warn("ProtectedRoute: LoadData API returned non-200 status:", response.status);
      }
    } catch (error) {
      console.error("ProtectedRoute: Error calling LoadData API:", error);
    }
  };

  // Ensure access token, userId, and roles are available when user is already authenticated
  useEffect(() => {
    const ensureAuthData = async () => {
      if (accounts.length > 0 && inProgress === InteractionStatus.None) {
        const userId = sessionStorage.getItem("userId");
        
        // If userId is missing or token is missing, validate user
        if (!userId || !accessToken) {
          setIsValidating(true);
          try {
            console.log("ProtectedRoute: Ensuring authentication data...");
            
            // Get or refresh access token
            const token = await getAccessToken(["User.Read"]);
            if (token && !accessToken) {
              dispatch(setAccessToken(token));
              console.log("ProtectedRoute: Access token stored in Redux");
            }

            // Validate user to get userId and roles
            if (!userId) {
              const isValid = await validateUser();
              if (!isValid) {
                console.error("ProtectedRoute: User validation failed");
                // Could redirect to access denied here if needed
              } else {
                // Call loadData API after successful validation
                await callLoadDataAPI(token);
              }
            }
          } catch (error) {
            console.error("ProtectedRoute: Error ensuring auth data:", error);
          } finally {
            setIsValidating(false);
          }
        }
      }
    };

    ensureAuthData();
  }, [accounts, inProgress, accessToken, getAccessToken, dispatch]);

  // If MSAL is still processing (startup or handling redirect), wait
  if (inProgress === InteractionStatus.Startup || 
      inProgress === InteractionStatus.HandleRedirect ||
      inProgress === InteractionStatus.Login) {
    return null;
  }

  // If validating user data, show nothing (or could show a loader)
  if (isValidating) {
    return null;
  }

  // If no accounts are found and no interaction is in progress, redirect to Authentication page
  if (accounts.length === 0 && inProgress === InteractionStatus.None) {
    // Store the intended destination in sessionStorage before redirecting
    sessionStorage.setItem("intendedDestination", location.pathname);
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }

  // User is authenticated, render the protected content
  return children;
}
